import React, { useState, useEffect } from 'react';
import SafeIcon from '../common/SafeIcon';
import { 
  FiUser, FiImage, FiSave, FiBriefcase, FiLock, 
  FiCheck, FiAlertCircle, FiShield, FiInfo
} from 'react-icons/fi';
import { motion, AnimatePresence } from 'framer-motion';
import { supabase } from '../supabase/supabase';

const ProfileView = ({ user, onSave, onNotify }) => {
  const [name, setName] = useState(user?.name || '');
  const [role, setRole] = useState(user?.role || '');
  const [avatar, setAvatar] = useState(user?.avatar || '');
  const [newPassword, setNewPassword] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [isChangingPass, setIsChangingPass] = useState(false);
  const [passStatus, setPassStatus] = useState({ type: '', msg: '' });
  const [imgError, setImgError] = useState(false);

  // Sync local state when user prop changes (e.g. after save)
  useEffect(() => {
    if (user) {
      setName(user.name || '');
      setRole(user.role || '');
      setAvatar(user.avatar || '');
      setImgError(false);
    }
  }, [user]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSaving(true);
    try {
      await onSave({ name, role, avatar });
    } catch (err) {
      onNotify('Failed to update profile', 'error');
    } finally {
      setIsSaving(false);
    }
  };

  const handlePasswordUpdate = async () => {
    if (!newPassword || newPassword.length < 6) {
      setPassStatus({ type: 'error', msg: 'Password must be at least 6 characters' });
      return;
    }
    setIsChangingPass(true);
    try {
      const { error } = await supabase.auth.updateUser({ password: newPassword });
      if (error) throw error;
      setPassStatus({ type: 'success', msg: 'Credentials Updated' });
      setNewPassword('');
      onNotify('Security Credentials Updated Successfully');
    } catch (err) {
      setPassStatus({ type: 'error', msg: err.message });
      onNotify(err.message, 'error');
    } finally {
      setIsChangingPass(false);
    }
  };

  return (
    <div className="p-8 max-w-4xl mx-auto">
      <div className="mb-10">
        <h1 className="text-2xl font-black text-slate-800 tracking-tight">Analyst Profile</h1>
        <p className="text-sm text-slate-500 font-medium">Manage your academy identity and security credentials</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="space-y-6">
          <div className="bg-white p-8 rounded-[2rem] border border-slate-200 shadow-sm text-center">
            <div className="relative w-32 h-32 mx-auto mb-6">
              <div className="w-full h-full rounded-[2.5rem] overflow-hidden border-4 border-slate-50 shadow-xl bg-slate-100 flex items-center justify-center">
                {avatar && !imgError ? (
                  <img 
                    src={avatar} 
                    alt="Profile" 
                    className="w-full h-full object-cover"
                    onError={() => setImgError(true)}
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-slate-300 text-4xl font-black bg-slate-100">
                    {name.charAt(0) || '?'}
                  </div>
                )}
              </div>
              <div className="absolute -bottom-2 -right-2 w-10 h-10 bg-blue-600 rounded-2xl flex items-center justify-center text-white shadow-lg border-4 border-white">
                <SafeIcon icon={FiShield} className="text-sm" />
              </div>
            </div>
            <h3 className="text-xl font-black text-slate-800 truncate">{name || 'New Agent'}</h3>
            <p className="text-xs font-black text-blue-600 uppercase tracking-widest mt-1">{role || 'Trainee'}</p>
            
            <div className="mt-8 pt-8 border-t border-slate-100 flex justify-center space-x-4">
              <div className="text-center">
                <div className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Status</div>
                <div className="text-xs font-bold text-emerald-600 bg-emerald-50 px-2 py-1 rounded-lg">Active</div>
              </div>
              <div className="w-px h-8 bg-slate-100" />
              <div className="text-center">
                <div className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Access</div>
                <div className="text-xs font-bold text-slate-700 bg-slate-50 px-2 py-1 rounded-lg">
                  {user?.tier2Access ? 'Senior' : 'Standard'}
                </div>
              </div>
            </div>
          </div>

          <div className="bg-slate-900 p-6 rounded-[2rem] text-white">
            <div className="flex items-center space-x-3 mb-4">
              <SafeIcon icon={FiInfo} className="text-blue-400" />
              <span className="text-[10px] font-black uppercase tracking-[0.2em]">Platform Note</span>
            </div>
            <p className="text-xs text-slate-400 leading-relaxed font-medium">
              {user?.isGuest 
                ? "Guest data is stored locally in this browser. Register to sync your profile across all devices."
                : "Your analyst profile is synced with the global network."}
            </p>
          </div>
        </div>

        <div className="lg:col-span-2 space-y-6">
          <form onSubmit={handleSubmit} className="bg-white p-8 rounded-[2rem] border border-slate-200 shadow-sm space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Display Name</label>
                <div className="relative">
                  <SafeIcon icon={FiUser} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
                  <input type="text" required value={name} onChange={(e) => setName(e.target.value)} className="w-full pl-11 pr-4 py-3 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-blue-500 transition-all text-sm font-bold text-slate-700" />
                </div>
              </div>
              <div className="space-y-2">
                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Job Title</label>
                <div className="relative">
                  <SafeIcon icon={FiBriefcase} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
                  <input type="text" required value={role} onChange={(e) => setRole(e.target.value)} className="w-full pl-11 pr-4 py-3 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-blue-500 transition-all text-sm font-bold text-slate-700" />
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Avatar Image URL</label>
              <div className="relative">
                <SafeIcon icon={FiImage} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
                <input type="url" value={avatar} onChange={(e) => { setAvatar(e.target.value); setImgError(false); }} placeholder="https://images.unsplash.com/..." className="w-full pl-11 pr-4 py-3 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-blue-500 transition-all text-sm font-bold text-slate-700" />
              </div>
            </div>

            <div className="pt-4 flex justify-end">
              <button type="submit" disabled={isSaving} className="px-8 py-3 bg-blue-600 text-white rounded-xl font-black uppercase tracking-widest text-xs hover:bg-blue-700 transition-all shadow-xl shadow-blue-200 flex items-center space-x-2 disabled:opacity-50">
                {isSaving ? <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <><SafeIcon icon={FiSave} /><span>Save Identity</span></>}
              </button>
            </div>
          </form>

          {!user?.isGuest && (
            <div className="bg-white p-8 rounded-[2rem] border border-slate-200 shadow-sm">
              <div className="flex items-center space-x-3 mb-6">
                <div className="w-10 h-10 bg-slate-100 rounded-xl flex items-center justify-center text-slate-400">
                  <SafeIcon icon={FiLock} />
                </div>
                <div>
                  <h4 className="text-sm font-black text-slate-800 uppercase tracking-tight">Security Credentials</h4>
                  <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest">Update your master access key</p>
                </div>
              </div>

              <div className="flex flex-col md:flex-row gap-4">
                <div className="flex-1 relative">
                  <SafeIcon icon={FiLock} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
                  <input type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} placeholder="New Secure Password" className="w-full pl-11 pr-4 py-3 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-blue-500 transition-all text-sm font-bold text-slate-700" />
                </div>
                <button onClick={handlePasswordUpdate} disabled={!newPassword || isChangingPass} className="px-6 py-3 bg-slate-900 text-white rounded-xl font-black uppercase tracking-widest text-[10px] hover:bg-slate-800 transition-all disabled:opacity-50">
                  {isChangingPass ? 'Updating...' : 'Update Key'}
                </button>
              </div>

              <AnimatePresence>
                {passStatus.msg && (
                  <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} className={`mt-4 text-[10px] font-black uppercase tracking-widest flex items-center space-x-2 ${passStatus.type === 'success' ? 'text-emerald-600' : 'text-red-500'}`}>
                    <SafeIcon icon={passStatus.type === 'success' ? FiCheck : FiAlertCircle} />
                    <span>{passStatus.msg}</span>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProfileView;